import { useCallback, useMemo, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { dateRangeFor, type DateFilterMode } from "../helpers/dateRange";

export interface TeleconsultoriaDateRange {
  /** Dia selecionado (no modo mês, qualquer dia do mês). */
  date: Dayjs;
  mode: DateFilterMode;
  /** Período para as buscas: YYYY-MM-DD. */
  startDate: string;
  endDate: string;
  /** O período contém hoje? (desabilita o botão "Hoje"). */
  isCurrentPeriod: boolean;
  setDate: (date: Dayjs) => void;
  setMode: (mode: DateFilterMode) => void;
  goToday: () => void;
  goPrevious: () => void;
  goNext: () => void;
}

/**
 * Estado do filtro de datas da Teleconsultoria (padrão Dia/Mês do ScheduleManage).
 * Cada lista usa o seu: o filtro dos horários não muda o das marcadas.
 * `initialDate` (opcional): dia inicial (ex.: o painel de marcação abre no dia da lista).
 */
export function useTeleconsultoriaDateRange(
  initialMode: DateFilterMode = "day",
  initialDate?: Dayjs,
) {
  const [date, setDateState] = useState<Dayjs>(() => (initialDate ?? dayjs()).startOf("day"));
  const [mode, setMode] = useState<DateFilterMode>(initialMode);

  const setDate = useCallback((next: Dayjs) => setDateState(next.startOf("day")), []);
  const goToday = useCallback(() => setDateState(dayjs().startOf("day")), []);
  const goPrevious = useCallback(
    () => setDateState((current) => current.subtract(1, mode)),
    [mode],
  );
  const goNext = useCallback(() => setDateState((current) => current.add(1, mode)), [mode]);

  return useMemo<TeleconsultoriaDateRange>(
    () => ({
      date,
      mode,
      ...dateRangeFor(date, mode),
      isCurrentPeriod: date.isSame(dayjs(), mode),
      setDate,
      setMode,
      goToday,
      goPrevious,
      goNext,
    }),
    [date, mode, setDate, goToday, goPrevious, goNext],
  );
}
